import type { ReactNode } from 'react';
import { organizationModuleCards } from '@brimo/shared';
import {
  Banner,
  Button,
  Card,
  Pill,
  Segmented,
  Skeleton,
  Table,
  type TableColumn,
} from '../../../../ds/components';
import { QueryError } from '../users/OrganizationPage';
import type { BillingPayer, OrganizationTree, TreeChild } from './api';
import styles from './OrganizationTreePage.module.css';

export type TreeState =
  { status: 'pending' } | { status: 'error'; error: unknown } | { status: 'ready'; tree: OrganizationTree };

export const TREE_TEXTS = {
  title: 'Organisationsbaum',
  description: 'Übergeordnete Organisation, Unterorganisationen und wer die Gebühren trägt.',
  parentTitle: 'Übergeordnete Organisation',
  parentNone: 'Keine — diese Organisation steht für sich.',
  parentNote:
    'Die übergeordnete Organisation sieht und bearbeitet alle Daten dieser Organisation (Servicing).',
  childrenTitle: 'Unterorganisationen',
  childrenEmpty: 'Noch keine Unterorganisation.',
  addChild: 'Unterorganisation hinzufügen',
  pkvTitle: 'PKV-Vorgaben',
  pkvOff: 'Schalten Sie das Modul PKV ein, um Vorgaben zu setzen.',
} as const;

export const billingPayerItems: ReadonlyArray<{ value: BillingPayer; label: string }> = [
  { value: 'self', label: 'Selbstzahler' },
  { value: 'parent', label: 'Wir tragen die Gebühren' },
];

function moduleName(key: string): string {
  return (organizationModuleCards as Record<string, { name: string } | undefined>)[key]?.name ?? key;
}

/** „Gebühren trägt White Label Agency" / „Selbstzahler" (the server's label) — only under a parent. */
export function ownPayerText(tree: OrganizationTree): string | null {
  return tree.parent === null ? null : tree.billingPayerLabel;
}

export interface OrganizationTreeViewProps {
  state: TreeState;
  /** Child whose billing payer or removal is being saved. */
  pendingChildId: string | null;
  onAddChild: () => void;
  onRemoveChild: (child: TreeChild) => void;
  onBillingPayerChange: (child: TreeChild, value: BillingPayer) => void;
  /** The block „PKV-Vorgaben" (banner, skeleton or form), composed by the page. */
  pkv: ReactNode;
}

/**
 * Einstellungen · Organisation „Organisationsbaum" (PKV M0, no design frame, D-288): the parent organization, the
 * children with their members, modules and billing payer, and the PKV defaults of the own organization.
 */
export function OrganizationTreeView({
  state,
  pendingChildId,
  onAddChild,
  onRemoveChild,
  onBillingPayerChange,
  pkv,
}: OrganizationTreeViewProps) {
  if (state.status === 'error') return <QueryError error={state.error} />;
  const tree = state.status === 'ready' ? state.tree : null;

  const columns: TableColumn<TreeChild>[] = [
    {
      id: 'name',
      header: 'Name',
      track: 'minmax(0, 1.2fr)',
      cell: (child) => <span className={styles.childName}>{child.name}</span>,
    },
    {
      id: 'slug',
      header: 'Slug',
      track: 'minmax(0, 0.8fr)',
      cell: (child) => <span className={styles.slug}>{child.slug}</span>,
    },
    { id: 'members', header: 'Mitglieder', track: '84px', numeric: true, cell: (child) => child.memberCount },
    {
      id: 'modules',
      header: 'Module',
      track: 'minmax(0, 1.4fr)',
      cell: (child) =>
        child.modules.length === 0 ? (
          <span className={styles.muted}>Keine</span>
        ) : (
          <span className={styles.pills}>
            {child.modules.map((key) => (
              <Pill key={key} variant={key === 'pkv' ? 'accent' : 'neutral'}>
                {moduleName(key)}
              </Pill>
            ))}
          </span>
        ),
    },
    {
      id: 'billing',
      header: 'Gebühren',
      track: '268px',
      cell: (child) => (
        <Segmented
          label={`Gebühren ${child.name}`}
          items={billingPayerItems.map((item) => ({ ...item, disabled: pendingChildId === child.id }))}
          value={child.billingPayer}
          onChange={(value) => {
            if (value !== child.billingPayer) onBillingPayerChange(child, value);
          }}
        />
      ),
    },
    {
      id: 'remove',
      header: <span className={styles.visuallyHidden}>Aktion</span>,
      track: '80px',
      align: 'end',
      cell: (child) => (
        <button
          type="button"
          className={styles.remove}
          disabled={pendingChildId === child.id}
          aria-label={`${child.name} entfernen`}
          onClick={() => onRemoveChild(child)}
        >
          Entfernen
        </button>
      ),
    },
  ];

  return (
    <div className={styles.blocks}>
      <section className={styles.block} aria-labelledby="tree-parent">
        <h3 id="tree-parent" className={styles.blockTitle}>
          {TREE_TEXTS.parentTitle}
        </h3>
        <div className={styles.parent}>
          {tree === null ? (
            <Skeleton width="40%" />
          ) : tree.parent ? (
            <div className={styles.parentRow}>
              <span className={styles.parentName}>{tree.parent.name}</span>
              <span className={styles.slug}>{tree.parent.slug}</span>
              <span className={styles.payer}>{ownPayerText(tree)}</span>
            </div>
          ) : (
            <span className={styles.parentNone}>{TREE_TEXTS.parentNone}</span>
          )}
          <p className={styles.note}>{TREE_TEXTS.parentNote}</p>
        </div>
      </section>

      <section className={styles.block} aria-labelledby="tree-children">
        <div className={styles.blockHead}>
          <h3 id="tree-children" className={styles.blockTitle}>
            {TREE_TEXTS.childrenTitle}
          </h3>
          <Button variant="secondary" size="small" disabled={tree === null} onClick={onAddChild}>
            {TREE_TEXTS.addChild}
          </Button>
        </div>
        <Card flush>
          {tree === null ? (
            <div className={styles.skeletonRows} aria-busy="true">
              <Skeleton width="55%" />
              <Skeleton width="40%" />
            </div>
          ) : (
            <Table
              label={TREE_TEXTS.childrenTitle}
              columns={columns}
              rows={tree.children}
              rowKey={(child) => child.id}
              density="compact"
              emptyText={TREE_TEXTS.childrenEmpty}
            />
          )}
        </Card>
      </section>

      <section className={styles.block} aria-labelledby="tree-pkv">
        <h3 id="tree-pkv" className={styles.blockTitle}>
          {TREE_TEXTS.pkvTitle}
        </h3>
        {pkv}
      </section>
    </div>
  );
}

/** The banner of the PKV block while the module is off; the link leads to „Module". */
export function PkvModuleOffBanner({ action }: { action: ReactNode }) {
  return (
    <Banner tone="info" actions={action}>
      {TREE_TEXTS.pkvOff}
    </Banner>
  );
}
